/**
 * Address / coordinate -> IANA timezone, for North America.
 *
 * NO DEPENDENCY, deliberately. A coordinate->tz library ships the whole
 * tzdata polygon set; this repo needs Canada + the US, where the
 * state/province already determines the zone for ~40 of 63 regions and
 * longitude settles the rest. Anything this cannot decide returns
 * `null` with a reason rather than a plausible guess — a wrong zone
 * silently mis-enforces charger access, which is the exact failure this
 * whole change exists to end. "Empty beats a wrong guess" is already
 * the house rule for Location.address.country.
 *
 * Used by scripts/backfill-location-timezones.ts and by My Home
 * (2026-10-02) through homeRegionZones below: Set up My Home compares the
 * phone's zone with the zones the address allows (sameCivilClock) and asks
 * the host which one to use when the phone keeps none of their clocks, and
 * POST /me/home falls back to the address zone, when the region has only
 * one, if the phone sent no usable zone. The charger page's "Use my
 * timezone" button does NOT cross-check: it writes the device zone as sent.
 */
import { US_STATE_CODES, CA_PROVINCE_CODES } from '../types/incentives.js';
import { isValidTimeZone, wallClockIn } from './localTime.js';
/** Regions with exactly one civil timezone. */
const SINGLE_ZONE = {
    // ── Canada ──────────────────────────────────────────────────────
    ON: 'America/Toronto', // Ontario is split at the Manitoba edge
    QC: 'America/Toronto', // (Atikokan/Thunder Bay) but every
    NS: 'America/Halifax', // populated EVI site is Eastern.
    NB: 'America/Moncton',
    PE: 'America/Halifax',
    NL: 'America/St_Johns',
    MB: 'America/Winnipeg',
    AB: 'America/Edmonton',
    BC: 'America/Vancouver',
    SK: 'America/Regina', // no DST
    YT: 'America/Whitehorse',
    NT: 'America/Yellowknife',
    // ── United States ───────────────────────────────────────────────
    AL: 'America/Chicago', AR: 'America/Chicago', CA: 'America/Los_Angeles',
    CO: 'America/Denver', CT: 'America/New_York', DC: 'America/New_York',
    DE: 'America/New_York', GA: 'America/New_York', HI: 'Pacific/Honolulu',
    IA: 'America/Chicago', IL: 'America/Chicago', LA: 'America/Chicago',
    MA: 'America/New_York', MD: 'America/New_York', ME: 'America/New_York',
    MN: 'America/Chicago', MO: 'America/Chicago', MS: 'America/Chicago',
    MT: 'America/Denver', NC: 'America/New_York', NH: 'America/New_York',
    NJ: 'America/New_York', NM: 'America/Denver', NV: 'America/Los_Angeles',
    NY: 'America/New_York', OH: 'America/New_York', OK: 'America/Chicago',
    PA: 'America/New_York', RI: 'America/New_York', SC: 'America/New_York',
    UT: 'America/Denver', VA: 'America/New_York', VT: 'America/New_York',
    WA: 'America/Los_Angeles', WI: 'America/Chicago', WV: 'America/New_York',
    WY: 'America/Denver',
};
/**
 * Regions straddling a zone boundary. Longitude decides; without
 * coordinates the resolver refuses (ambiguous) rather than picking the
 * more populous half — a charger on the wrong side would be an hour
 * out, every day, invisibly.
 */
const SPLIT_ZONE = {
    // US
    FL: { west: 'America/Chicago', east: 'America/New_York', boundaryLng: -85.0 },
    IN: { west: 'America/Chicago', east: 'America/Indiana/Indianapolis', boundaryLng: -87.3 },
    KY: { west: 'America/Chicago', east: 'America/New_York', boundaryLng: -85.9 },
    MI: { west: 'America/Menominee', east: 'America/Detroit', boundaryLng: -87.6 },
    TN: { west: 'America/Chicago', east: 'America/New_York', boundaryLng: -85.3 },
    TX: { west: 'America/Denver', east: 'America/Chicago', boundaryLng: -105.0 },
    KS: { west: 'America/Denver', east: 'America/Chicago', boundaryLng: -101.9 },
    NE: { west: 'America/Denver', east: 'America/Chicago', boundaryLng: -101.5 },
    ND: { west: 'America/Denver', east: 'America/Chicago', boundaryLng: -101.0 },
    SD: { west: 'America/Denver', east: 'America/Chicago', boundaryLng: -100.5 },
    OR: { west: 'America/Los_Angeles', east: 'America/Boise', boundaryLng: -117.2 },
    ID: { west: 'America/Los_Angeles', east: 'America/Boise', boundaryLng: -116.0 },
    AZ: { west: 'America/Phoenix', east: 'America/Phoenix', boundaryLng: -109.0 }, // Navajo Nation observes DST
    AK: { west: 'America/Anchorage', east: 'America/Anchorage', boundaryLng: -141.0 },
    // Canada
    NU: { west: 'America/Cambridge_Bay', east: 'America/Iqaluit', boundaryLng: -95.0 },
};
function normalizeRegion(state) {
    if (!state)
        return null;
    const s = state.trim().toUpperCase();
    if (s.length === 2)
        return s;
    // Long-form names — real address data carries both forms, and a
    // missing entry here shows up as a REFUSED row in the backfill
    // (which is how these were found), never as a wrong zone.
    const LONG = {
        // Canada
        ONTARIO: 'ON', QUEBEC: 'QC', 'QUÉBEC': 'QC', 'BRITISH COLUMBIA': 'BC',
        ALBERTA: 'AB', MANITOBA: 'MB', SASKATCHEWAN: 'SK', 'NOVA SCOTIA': 'NS',
        'NEW BRUNSWICK': 'NB', 'PRINCE EDWARD ISLAND': 'PE',
        'NEWFOUNDLAND AND LABRADOR': 'NL', YUKON: 'YT',
        'NORTHWEST TERRITORIES': 'NT', NUNAVUT: 'NU',
        // United States
        ALABAMA: 'AL', ALASKA: 'AK', ARIZONA: 'AZ', ARKANSAS: 'AR',
        CALIFORNIA: 'CA', COLORADO: 'CO', CONNECTICUT: 'CT', DELAWARE: 'DE',
        'DISTRICT OF COLUMBIA': 'DC', FLORIDA: 'FL', GEORGIA: 'GA',
        HAWAII: 'HI', IDAHO: 'ID', ILLINOIS: 'IL', INDIANA: 'IN', IOWA: 'IA',
        KANSAS: 'KS', KENTUCKY: 'KY', LOUISIANA: 'LA', MAINE: 'ME',
        MARYLAND: 'MD', MASSACHUSETTS: 'MA', MICHIGAN: 'MI', MINNESOTA: 'MN',
        MISSISSIPPI: 'MS', MISSOURI: 'MO', MONTANA: 'MT', NEBRASKA: 'NE',
        NEVADA: 'NV', 'NEW HAMPSHIRE': 'NH', 'NEW JERSEY': 'NJ',
        'NEW MEXICO': 'NM', 'NEW YORK': 'NY', 'NORTH CAROLINA': 'NC',
        'NORTH DAKOTA': 'ND', OHIO: 'OH', OKLAHOMA: 'OK', OREGON: 'OR',
        PENNSYLVANIA: 'PA', 'RHODE ISLAND': 'RI', 'SOUTH CAROLINA': 'SC',
        'SOUTH DAKOTA': 'SD', TENNESSEE: 'TN', TEXAS: 'TX', UTAH: 'UT',
        VERMONT: 'VT', VIRGINIA: 'VA', WASHINGTON: 'WA',
        'WEST VIRGINIA': 'WV', WISCONSIN: 'WI', WYOMING: 'WY',
    };
    return LONG[s] ?? null;
}
/**
 * Resolve a site's timezone. Coordinates are used ONLY to disambiguate
 * a split region — never as a primary source, because deployed
 * coordinates are human-entered (the geocoder is unconfigured in
 * deployed environments) and a typo in a lat/lng should not silently
 * relocate a charger's clock.
 */
export function resolveTimeZoneFromAddress(address) {
    const region = normalizeRegion(address.state);
    if (!region) {
        return {
            timezone: null,
            confidence: 'unknown',
            reason: `state/province "${address.state ?? ''}" not recognized`,
        };
    }
    const single = SINGLE_ZONE[region];
    if (single) {
        return { timezone: single, confidence: 'certain', reason: `${region} has one timezone` };
    }
    const split = SPLIT_ZONE[region];
    if (split) {
        const lng = address.coordinates?.lng;
        if (typeof lng !== 'number' || Number.isNaN(lng)) {
            return {
                timezone: null,
                confidence: 'ambiguous',
                reason: `${region} spans two timezones and the location has no longitude`,
            };
        }
        const zone = lng < split.boundaryLng ? split.west : split.east;
        return {
            timezone: zone,
            confidence: zone === split.west || zone === split.east ? 'certain' : 'ambiguous',
            reason: `${region} split at lng ${split.boundaryLng}; site at ${lng}`,
        };
    }
    return {
        timezone: null,
        confidence: 'unknown',
        reason: `no timezone rule for region "${region}"`,
    };
}
// ── Home time zones (Set up My Home and Home Settings, 2026-10-02) ─────────
/**
 * The zones a home in `state` (a code or a full name) can be on, judged
 * from the region alone, without coordinates: the one zone of a
 * single-zone region; both halves of a split region, or one when the
 * halves share a zone (Arizona, Alaska); none for an unknown region.
 * Exactly one zone means the address settles the clock.
 */
export function homeRegionZones(state) {
    const region = normalizeRegion(state);
    if (!region)
        return [];
    const single = SINGLE_ZONE[region];
    if (single)
        return [single];
    const split = SPLIT_ZONE[region];
    if (!split)
        return [];
    return split.west === split.east ? [split.west] : [split.west, split.east];
}
/**
 * UTC offset of `timeZone` at `at`, in minutes east of UTC (Toronto in
 * winter is -300). Null when the zone does not resolve, so a caller
 * never compares against the server's own clock by accident.
 */
function utcOffsetMinutes(timeZone, at) {
    if (!isValidTimeZone(timeZone))
        return null;
    const w = wallClockIn(at, timeZone);
    const asUtc = Date.UTC(w.year, w.month - 1, w.dayOfMonth, w.hour, w.minute);
    return Math.round((asUtc - Math.floor(at.getTime() / 60_000) * 60_000) / 60_000);
}
/**
 * Zones some homes keep that no region rule above returns, because each
 * covers communities the rules round to their province's or state's main
 * zone. Listed so Home Settings can offer them by name.
 */
const EXTRA_HOME_ZONES = {
    // The Aleutians west of 169.5 W: Hawaii-Aleutian time, with DST.
    US: ['America/Adak'],
    CA: [
        // Northeast BC (Dawson Creek, Fort St. John, Fort Nelson) and the
        // Creston valley: Mountain Standard Time all year.
        'America/Creston', 'America/Dawson_Creek', 'America/Fort_Nelson',
        // Atikokan, northwest Ontario: Eastern Standard Time all year, a clock
        // no other Canadian entry keeps.
        'America/Atikokan',
    ],
};
/**
 * The zones a home may pick, per country: every zone the resolver above
 * can return for that country's regions, so the picker never misses one
 * the address rule would choose, plus EXTRA_HOME_ZONES. West to east by
 * winter offset, then by name.
 */
function zonesFor(codes, extras) {
    const set = new Set(extras);
    for (const code of codes) {
        const single = SINGLE_ZONE[code];
        if (single)
            set.add(single);
        const split = SPLIT_ZONE[code];
        if (split) {
            set.add(split.west);
            set.add(split.east);
        }
    }
    const winter = new Date(Date.UTC(2026, 0, 15, 12));
    const offset = (z) => utcOffsetMinutes(z, winter) ?? Number.MAX_SAFE_INTEGER;
    return [...set].sort((a, b) => offset(a) - offset(b) || a.localeCompare(b));
}
export const HOME_TIME_ZONES = {
    US: zonesFor(US_STATE_CODES, EXTRA_HOME_ZONES.US),
    CA: zonesFor(CA_PROVINCE_CODES, EXTRA_HOME_ZONES.CA),
};
const GEOGRAPHIC_AREA = /^(Africa|America|Antarctica|Arctic|Asia|Atlantic|Australia|Europe|Indian|Pacific)\//;
/**
 * True for a real place's zone ("America/Toronto"): one this runtime can
 * resolve, named Area/Location. UTC, GMT, Etc/* and the bare abbreviations
 * (EST, EST5EDT) are refused: a privacy browser reports UTC whatever its
 * real location, and a home's clock must be a place's clock.
 */
export function isGeographicHomeZone(timeZone) {
    if (!timeZone || timeZone.length > 64)
        return false;
    return GEOGRAPHIC_AREA.test(timeZone) && isValidTimeZone(timeZone);
}
/**
 * True when two zones keep the same civil clock: the same UTC offset in
 * mid-January and mid-July of `at`'s year. Toronto and New York agree;
 * Regina and Winnipeg do not (they part in summer). An unresolvable zone
 * agrees with nothing.
 */
export function sameCivilClock(a, b, at = new Date()) {
    if (a === b)
        return isValidTimeZone(a);
    const year = at.getUTCFullYear();
    for (const month of [0, 6]) {
        const instant = new Date(Date.UTC(year, month, 15, 12));
        const oa = utcOffsetMinutes(a, instant);
        const ob = utcOffsetMinutes(b, instant);
        if (oa === null || ob === null || oa !== ob)
            return false;
    }
    return true;
}
/** Cities whose IANA spelling is not how they are written. Coral_Harbour
 *  is the name browsers report for Atikokan's zone. */
const CITY_LABELS = {
    'America/St_Johns': "St. John's",
    'America/Coral_Harbour': 'Atikokan',
};
/** "America/Toronto" -> "Toronto", "America/Indiana/Indianapolis" ->
 *  "Indianapolis". The IANA city, never an invented abbreviation. */
export function timeZoneCityLabel(timeZone) {
    const named = CITY_LABELS[timeZone];
    if (named)
        return named;
    const city = timeZone.split('/').pop();
    return city ? city.replace(/_/g, ' ') : timeZone;
}
/**
 * The runtime's own name for a zone, so two spellings of one zone compare
 * equal: browsers report America/Indianapolis for
 * America/Indiana/Indianapolis, and America/Coral_Harbour for
 * America/Atikokan. A zone the runtime cannot resolve is returned as is.
 */
export function canonicalTimeZone(timeZone) {
    try {
        return new Intl.DateTimeFormat('en-US', { timeZone }).resolvedOptions().timeZone;
    }
    catch {
        return timeZone;
    }
}
//# sourceMappingURL=regionTimeZone.js.map